import { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function ProfilePage() {
    const { user } = useAuth();
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.current_password || !form.new_password) {
            toast.error("Both current and new passwords are required");
            return;
        }
        if (form.new_password.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        if (form.new_password !== form.confirm) {
            toast.error("Confirmation does not match");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Password updated successfully");
            setForm({ current_password: "", new_password: "", confirm: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update password");
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) return null;

    return (
        <div data-testid="profile-page">
            <PageHeader title="My Profile" eyebrow="Account" description="Manage your account details and password." />

            <div className="grid lg:grid-cols-3 gap-3">
                <div className="bg-white border border-slate-200 p-5 lg:col-span-1" data-testid="profile-info-card">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-12 h-12 bg-[#002FA7] text-white text-lg font-semibold flex items-center justify-center rounded-sm">
                            {(user.name || "U").slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                            <div className="text-[16px] font-semibold tracking-tight">{user.name}</div>
                            <div className="text-[12px] font-mono text-slate-500">{user.email}</div>
                        </div>
                    </div>
                    <div className="space-y-3 text-[13px]">
                        <Row label="Role">
                            <span className="text-[11px] uppercase tracking-widest bg-slate-100 px-2 py-0.5 font-mono">
                                {user.role}
                            </span>
                        </Row>
                        <Row label="Status">
                            {user.active !== false ? (
                                <span className="text-emerald-700 text-[12px] font-medium">Active</span>
                            ) : (
                                <span className="text-red-700 text-[12px] font-medium">Inactive</span>
                            )}
                        </Row>
                        <Row label="User ID">
                            <span className="font-mono text-[11px] text-slate-500">{user.id}</span>
                        </Row>
                    </div>
                    <div className="mt-6 pt-4 border-t border-slate-100">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Module access</div>
                        <div className="flex flex-wrap gap-1.5">
                            {Object.entries(user.permissions || {})
                                .filter(([, actions]) => (actions || []).length > 0)
                                .map(([m, actions]) => (
                                    <span
                                        key={m}
                                        className="text-[10.5px] font-mono bg-slate-50 border border-slate-200 px-1.5 py-0.5"
                                    >
                                        {m}
                                        <span className="text-slate-400 ml-1">{actions.join("/")}</span>
                                    </span>
                                ))}
                            {user.role === "admin" && (
                                <span className="text-[10.5px] font-mono bg-blue-50 border border-blue-200 text-blue-800 px-1.5 py-0.5">
                                    <ShieldCheck size={10} className="inline mr-0.5" /> All modules
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="bg-white border border-slate-200 p-5 lg:col-span-2"
                    data-testid="password-form"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <KeyRound size={16} className="text-[#002FA7]" />
                        <h2 className="text-lg font-semibold tracking-tight">Reset password</h2>
                    </div>
                    <p className="text-[12.5px] text-slate-500 mb-5">
                        Choose a strong password — minimum 8 characters. You will remain signed in after the change.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
                        <div className="md:col-span-2">
                            <Field label="Current password">
                                <Input
                                    type="password"
                                    value={form.current_password}
                                    onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                                    className="rounded-sm h-10"
                                    autoComplete="current-password"
                                    data-testid="profile-current-password"
                                    required
                                />
                            </Field>
                        </div>
                        <Field label="New password">
                            <Input
                                type="password"
                                value={form.new_password}
                                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                                className="rounded-sm h-10"
                                autoComplete="new-password"
                                data-testid="profile-new-password"
                                required
                                minLength={8}
                            />
                        </Field>
                        <Field label="Confirm new password">
                            <Input
                                type="password"
                                value={form.confirm}
                                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                className="rounded-sm h-10"
                                autoComplete="new-password"
                                data-testid="profile-confirm-password"
                                required
                                minLength={8}
                            />
                        </Field>
                    </div>

                    <div className="mt-6 flex items-center gap-3">
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f] h-10 px-5"
                            data-testid="profile-save-password-btn"
                        >
                            {submitting ? "Updating…" : "Update password"}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setForm({ current_password: "", new_password: "", confirm: "" })}
                            className="h-10"
                            data-testid="profile-reset-form-btn"
                        >
                            Clear
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
            <span>{children}</span>
        </div>
    );
}
